import { api_fetch } from "./client.tsx"

export type AccountingWizardPath =
  | "no_backup_rule_auto_code"
  | "no_backup_no_rule"
  | "no_backup_gemini"
  | "needs_backup"
  | "unanimous_no_gemini"
  | "gemini_full_list"
  | "gemini_locked_reason_code"
  | "manual_recode"
  | "error"

export interface UserFileBackupCandidate {
  s3_uri: string
  display_name: string
}

/** The ledger account CodingWiz applied this run (for pre-filling the account picker). */
export interface AppliedLedgerAccount {
  id: string
  account_number: string
  expense_account: string
}

/** What CodingWiz did for execution date and retailer (values + human-readable why). */
export interface LedgerWizAppliedFields {
  /** YYYY-MM-DD when written this run; omit when unchanged / not set. */
  execution_date?: string | null
  execution_date_note: string
  retailer_name?: string | null
  retailer_note: string
}

export interface AccountingWizardOutcome {
  deduction_id: string
  path: AccountingWizardPath
  summary_bullets: string[]
  summary_markdown: string
  model_reasoning?: string | null
  success: boolean
  error?: string | null
  /** Present when there is no official backup yet but the deduction has user-uploaded files. */
  user_files_as_backup_candidates?: UserFileBackupCandidate[] | null
  applied_fields?: LedgerWizAppliedFields | null
  /** The ledger account applied this run; pre-fills the account picker for editing. */
  applied_account?: AppliedLedgerAccount | null
}

export async function run_accounting_wizard(deduction_id: string, options?: RunAccountingWizardOptions) {
  return api_fetch<AccountingWizardOutcome>("/accounting/wizard/run", {
    method: "POST",
    body: {
      deduction_id,
      feedback: options?.feedback?.trim() || null,
    },
  })
}

export interface RunAccountingWizardOptions {
  feedback?: string
}

export type AccountingWizardStreamEvent =
  | { type: "progress"; message: string }
  | { type: "thinking_delta"; content: string }
  | { type: "thinking_done" }
  | { type: "outcome"; outcome: AccountingWizardOutcome }
  | { type: "error"; error: string }

export interface AccountingWizardStreamCallbacks {
  on_progress?: (message: string) => void
  on_thinking_delta?: (content: string) => void
  on_thinking_done?: () => void
  on_outcome: (outcome: AccountingWizardOutcome) => void
  on_error: (message: string) => void
}

function parse_accounting_wizard_stream_event(raw: unknown): AccountingWizardStreamEvent | null {
  if (!raw || typeof raw !== "object") {
    return null
  }
  const o = raw as Record<string, unknown>
  const t = o.type
  if (t === "progress" && typeof o.message === "string") {
    return { type: "progress", message: o.message }
  }
  if (t === "thinking_delta" && typeof o.content === "string") {
    return { type: "thinking_delta", content: o.content }
  }
  if (t === "thinking_done") {
    return { type: "thinking_done" }
  }
  if (t === "outcome" && o.outcome && typeof o.outcome === "object") {
    return { type: "outcome", outcome: o.outcome as AccountingWizardOutcome }
  }
  if (t === "error" && typeof o.error === "string") {
    return { type: "error", error: o.error }
  }
  return null
}

/**
 * POST `/accounting/wizard/run-stream` — SSE with progress, optional thinking deltas, then final outcome.
 */
export async function run_accounting_wizard_stream(
  deduction_id: string,
  callbacks: AccountingWizardStreamCallbacks,
  options?: RunAccountingWizardOptions,
  signal?: AbortSignal
): Promise<void> {
  const response = await fetch("/api/accounting/wizard/run-stream", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
    },
    credentials: "same-origin",
    body: JSON.stringify({
      deduction_id,
      feedback: options?.feedback?.trim() || null,
    }),
    signal,
  })

  if (!response.ok) {
    const text = await response.text().catch(() => "")
    callbacks.on_error(text || `Request failed (${response.status})`)
    return
  }

  const reader = response.body?.getReader()
  if (!reader) {
    callbacks.on_error("No response body")
    return
  }

  const decoder = new TextDecoder()
  let buffer = ""

  while (true) {
    const { done, value } = await reader.read()
    if (done) {
      break
    }

    buffer += decoder.decode(value, { stream: true })
    const lines = buffer.split("\n")
    buffer = lines.pop() || ""

    for (const line of lines) {
      if (!line.startsWith("data: ")) {
        continue
      }
      const data = line.slice(6).trim()
      if (!data || data === "keep-alive") {
        continue
      }
      try {
        const raw: unknown = JSON.parse(data)
        const ev = parse_accounting_wizard_stream_event(raw)
        if (!ev) {
          continue
        }
        switch (ev.type) {
          case "progress":
            callbacks.on_progress?.(ev.message)
            break
          case "thinking_delta":
            callbacks.on_thinking_delta?.(ev.content)
            break
          case "thinking_done":
            callbacks.on_thinking_done?.()
            break
          case "outcome":
            callbacks.on_outcome(ev.outcome)
            break
          case "error":
            callbacks.on_error(ev.error)
            break
        }
      } catch {
        // skip malformed JSON
      }
    }
  }
}

export interface AccountingWizardBulkResponse {
  results: AccountingWizardOutcome[]
}

export interface BulkAccountingWizardOptions {
  feedback?: string
}

export async function bulk_accounting_wizard(
  deduction_ids: string[],
  options?: BulkAccountingWizardOptions
) {
  return api_fetch<AccountingWizardBulkResponse>("/accounting/wizard/bulk", {
    method: "POST",
    body: {
      deduction_ids,
      feedback: options?.feedback?.trim() || null,
    },
  })
}

/**
 * Manually re-code a deduction to a chosen ledger account (used when the right
 * account wasn't in the AI's candidate list). Reuses the same write CodingWiz
 * performs and returns a fresh outcome describing the manual choice.
 */
export async function recode_deduction_account(deduction_id: string, account_id: string) {
  return api_fetch<AccountingWizardOutcome>("/accounting/wizard/recode", {
    method: "POST",
    body: { deduction_id, account_id },
  })
}

export interface CreatedLedgerAccount {
  id: string
  account_number: string
  expense_account: string
}

/** Create a new ledger account (GL reason code) for the org, inline from CodingWiz. */
export async function create_ledger_account(account_number: string, expense_account: string) {
  return api_fetch<CreatedLedgerAccount>("/organization/ledger-accounts", {
    method: "POST",
    body: { account_number: account_number.trim(), expense_account: expense_account.trim() },
  })
}

/** Promotes an existing user file URI to `deduction.backup_s3_uri` (must already be in `user_files`). */
export async function set_backup_from_user_file(
  deduction_id: string,
  s3_uri: string,
  options?: { confirm_replace_distributor_backup?: boolean }
) {
  return api_fetch<Record<string, never>>(
    `/deduction/${encodeURIComponent(deduction_id)}/set-backup-from-user-file`,
    {
      method: "POST",
      body: {
        s3_uri,
        confirm_replace_distributor_backup: options?.confirm_replace_distributor_backup ?? false,
      },
    }
  )
}
